import { useEffect, useRef, useState } from "react";
import { MapPin, Navigation, Home, Package, Loader2 } from "lucide-react";

// Simple SVG-based map component (no external map library needed)
// For production, replace with Google Maps, Mapbox, or Leaflet
const LiveTrackMap = ({ routePoints, courierLocation, isLive, status }) => {
  const canvasRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 400 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const { width, height } = dimensions;
    canvas.width = width;
    canvas.height = height;

    // Clear
    ctx.clearRect(0, 0, width, height);

    // Background
    ctx.fillStyle = "#f8fafc";
    ctx.fillRect(0, 0, width, height);

    // Grid lines
    ctx.strokeStyle = "#e2e8f0";
    ctx.lineWidth = 1;
    for (let i = 0; i < width; i += 50) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, height);
      ctx.stroke();
    }
    for (let i = 0; i < height; i += 50) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(width, i);
      ctx.stroke();
    }

    if (!routePoints || routePoints.length === 0) {
      // No data - draw placeholder
      ctx.fillStyle = "#94a3b8";
      ctx.font = "16px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("No location data available", width / 2, height / 2);
      return;
    }

    // Calculate bounds
    const lats = routePoints.map((p) => p.lat).filter(Boolean);
    const lngs = routePoints.map((p) => p.lng).filter(Boolean);

    if (lats.length === 0 || lngs.length === 0) return;

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    const latRange = maxLat - minLat || 0.01;
    const lngRange = maxLng - minLng || 0.01;

    const padding = 60;
    const mapWidth = width - padding * 2;
    const mapHeight = height - padding * 2;

    const toX = (lng) => padding + ((lng - minLng) / lngRange) * mapWidth;
    const toY = (lat) =>
      height - padding - ((lat - minLat) / latRange) * mapHeight;

    // Draw route line
    if (routePoints.length >= 2) {
      ctx.strokeStyle = "#3b82f6";
      ctx.lineWidth = 3;
      ctx.setLineDash([8, 4]);
      ctx.beginPath();
      routePoints.forEach((point, i) => {
        const x = toX(point.lng);
        const y = toY(point.lat);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Draw points
    routePoints.forEach((point, index) => {
      const x = toX(point.lng);
      const y = toY(point.lat);

      // Point circle
      ctx.beginPath();
      ctx.arc(x, y, 8, 0, Math.PI * 2);

      if (point.type === "pickup") {
        ctx.fillStyle = "#f59e0b";
        ctx.strokeStyle = "#d97706";
      } else if (point.type === "delivery") {
        ctx.fillStyle = "#10b981";
        ctx.strokeStyle = "#059669";
      } else if (point.type === "courier") {
        ctx.fillStyle = "#3b82f6";
        ctx.strokeStyle = "#2563eb";
      } else {
        ctx.fillStyle = "#6b7280";
        ctx.strokeStyle = "#4b5563";
      }

      ctx.lineWidth = 2;
      ctx.fill();
      ctx.stroke();

      // Label
      ctx.fillStyle = "#1f2937";
      ctx.font = "bold 12px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(point.label || `Point ${index + 1}`, x, y - 15);

      // Pulse effect for courier
      if (point.type === "courier" && isLive) {
        const time = Date.now() / 1000;
        const radius = 12 + Math.sin(time * 3) * 4;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(59, 130, 246, 0.4)";
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    });

    // Legend
    ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
    ctx.fillRect(10, 10, 140, 90);
    ctx.strokeStyle = "#e2e8f0";
    ctx.lineWidth = 1;
    ctx.strokeRect(10, 10, 140, 90);

    const legendItems = [
      { color: "#f59e0b", label: "Pickup" },
      { color: "#10b981", label: "Delivery" },
      { color: "#3b82f6", label: "Courier" },
    ];

    legendItems.forEach((item, i) => {
      ctx.beginPath();
      ctx.arc(25, 30 + i * 22, 6, 0, Math.PI * 2);
      ctx.fillStyle = item.color;
      ctx.fill();
      ctx.fillStyle = "#374151";
      ctx.font = "11px sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(item.label, 38, 34 + i * 22);
    });
  }, [routePoints, courierLocation, isLive, dimensions, status]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      const container = canvasRef.current?.parentElement;
      if (container) {
        setDimensions({
          width: container.clientWidth,
          height: Math.min(400, container.clientWidth * 0.5),
        });
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="relative w-full rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
      <canvas
        ref={canvasRef}
        style={{ width: "100%", height: dimensions.height }}
        className="block"
      />

      {isLive && (
        <div className="absolute top-3 right-3 flex items-center space-x-2 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-sm border border-gray-200">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
          </span>
          <span className="text-xs font-medium text-emerald-700">Live</span>
        </div>
      )}

      {courierLocation && (
        <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-sm border border-gray-200 text-xs">
          <div className="flex items-center space-x-2 text-gray-600">
            <Navigation className="w-3.5 h-3.5 text-blue-500" />
            <span>
              Lat: {courierLocation.lat?.toFixed(4)}, Lng:{" "}
              {courierLocation.lng?.toFixed(4)}
            </span>
          </div>
          {courierLocation.last_updated_seconds && (
            <p className="text-gray-400 mt-0.5">
              Updated{" "}
              {courierLocation.last_updated_seconds < 60
                ? "just now"
                : `${Math.floor(courierLocation.last_updated_seconds / 60)}m ago`}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default LiveTrackMap;
